import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { ClipLoader } from 'react-spinners';
import {
  PencilSquareIcon,
  PlusIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  MapPinIcon,
  CheckBadgeIcon,
  TagIcon,
  BuildingOffice2Icon,
  IdentificationIcon,
  ArrowRightIcon,
  LinkIcon,
  XMarkIcon,
  CheckIcon,
  LockClosedIcon,
  CameraIcon,
} from '@heroicons/react/24/outline';

import { useAlert } from 'context/AlertContext';
import { useImageCropper } from 'hooks/useImageCropper';
import ImageCropper from 'components/ImageCropper';
import {
  validateFiles,
  uploadErrorMessage,
  IMAGE_ACCEPT,
} from 'utils/uploadValidation';

import { updatePromoter } from '../../../redux/actions/promoter';

import { isNameTakenError } from 'utils/nameTaken';
import facebookIcon from '../../../assets/icons/socials/facebook_icon.svg';
import instagramIcon from '../../../assets/icons/socials/instagram_icon.svg';
import linkedinIcon from '../../../assets/icons/socials/linkedin_icon.svg';
import twitterIcon from '../../../assets/icons/socials/twitter_icon.svg';

const SOCIAL_ICONS = {
  facebook: facebookIcon,
  instagram: instagramIcon,
  linkedin: linkedinIcon,
  twitter: twitterIcon,
};

const initialsOf = (str) => {
  if (!str || typeof str !== 'string') return '?';
  const parts = str.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0).toUpperCase()).join('') || '?';
};

// eslint-disable-next-line no-unused-vars
const computeCompleteness = (p) => {
  if (!p) return 0;
  const checks = [
    !!p.username,
    !!p.email,
    !!(p.city || p.country),
    !!p.companyName,
    !!p.fiscalNumber,
    !!p.firm,
    !!p.headquarters,
    !!p.profileInfo,
    !!p.avatar,
  ];
  const filled = checks.filter(Boolean).length;
  return Math.round((filled / checks.length) * 100);
};

const Eyebrow = ({ children }) => (
  <div className="text-[11px] uppercase tracking-[0.14em] text-muted font-bold mb-1.5">
    {children}
  </div>
);

const TabHeader = ({ eyebrow, title, sub, actions }) => (
  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 md:gap-6 mb-6 pb-5 border-b border-line">
    <div className="min-w-0">
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="text-[26px] sm:text-[30px] md:text-[34px] leading-[1.05] tracking-tight text-ink-strong font-bold break-words">
        {title}
      </h2>
      {sub && (
        <p className="text-[14px] text-muted mt-2 max-w-xl leading-relaxed">
          {sub}
        </p>
      )}
    </div>
    {actions && (
      <div className="flex items-center gap-2 flex-wrap">{actions}</div>
    )}
  </div>
);

const Card = ({ children, className = '', padded = true }) => (
  <section
    className={`bg-white border border-line rounded-2xl shadow-softer ${
      padded ? 'p-6' : ''
    } ${className}`}
  >
    {children}
  </section>
);

const CardHeader = ({ eyebrow, title, sub, action }) => (
  <header className="flex items-start justify-between gap-4 mb-5">
    <div className="min-w-0">
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h3 className="text-[17px] sm:text-[19px] font-bold tracking-tight text-ink-strong leading-snug">
        {title}
      </h3>
      {sub && (
        <p className="text-sm text-muted mt-1 max-w-prose">{sub}</p>
      )}
    </div>
    {action}
  </header>
);

// eslint-disable-next-line no-unused-vars
const Progress = ({ value }) => (
  <div className="h-1.5 rounded-full bg-line overflow-hidden">
    <div
      className="h-full bg-brand rounded-full"
      style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
    />
  </div>
);

const PencilBtn = ({ onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className="w-9 h-9 rounded-full hover:bg-paper-2 inline-flex items-center justify-center text-ink shrink-0"
  >
    <PencilSquareIcon className="w-[18px] h-[18px]" />
  </button>
);

const EditActions = ({ onCancel, onSave, saving }) => {
  const { t } = useTranslation();
  return (
    <div className="mt-5 pt-4 border-t border-line flex items-center justify-end gap-2">
      <button
        type="button"
        onClick={onCancel}
        disabled={saving}
        className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-ink hover:bg-paper-2 text-[13px] font-bold disabled:opacity-50"
      >
        <XMarkIcon className="w-4 h-4" />
        {t('dashboard.cancel')}
      </button>
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-brand hover:bg-brand-hover text-ink-strong text-[13px] font-bold shadow-softer disabled:opacity-60"
      >
        {saving ? (
          <ClipLoader color="#1A1A1F" size={14} />
        ) : (
          <CheckIcon className="w-4 h-4" />
        )}
        {saving ? t('dashboard.saving') : t('dashboard.save')}
      </button>
    </div>
  );
};

const TextInput = (props) => (
  <input
    type="text"
    {...props}
    className={`w-full h-11 px-3.5 rounded-xl bg-white border border-line text-[14px] text-ink placeholder:text-muted focus:outline-none focus:border-yellow-03 transition-colors ${
      props.className || ''
    }`}
  />
);

const Textarea = (props) => (
  <textarea
    {...props}
    className={`w-full min-h-[120px] px-3.5 py-3 rounded-xl bg-white border border-line text-[14px] text-ink placeholder:text-muted focus:outline-none focus:border-yellow-03 transition-colors resize-y ${
      props.className || ''
    }`}
  />
);

const FieldLabel = ({ children }) => (
  <div className="text-[10.5px] uppercase tracking-[0.08em] text-yellow-03 font-bold mb-1.5">
    {children}
  </div>
);

const InfoCell = ({ icon: Icon, label, value, href, mono, locked }) => {
  const empty = !value;
  const body = (
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg bg-brand-softer flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-yellow-03" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10.5px] uppercase tracking-[0.08em] text-yellow-03 font-bold flex items-center gap-1.5">
          {label}
          {locked && <LockClosedIcon className="w-3 h-3 text-muted" />}
        </div>
        <div
          className={`text-[13.5px] truncate ${
            empty
              ? 'text-muted italic'
              : href
              ? 'text-ink-strong underline underline-offset-2 decoration-line'
              : 'text-ink'
          } ${mono ? 'font-mono' : ''}`}
          title={value || ''}
        >
          {value || '—'}
        </div>
      </div>
    </div>
  );

  if (href && value) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="block hover:opacity-90"
      >
        {body}
      </a>
    );
  }
  return body;
};

const PromoterInfo = ({ promoter, loading, error, onEdit }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showAlert } = useAlert();

  const [editingSection, setEditingSection] = useState(null);
  const [saving, setSaving] = useState(false);

  const [identityForm, setIdentityForm] = useState({ username: '' });
  const [aboutForm, setAboutForm] = useState({ profileInfo: '' });
  const [businessForm, setBusinessForm] = useState({
    companyName: '',
    fiscalNumber: '',
    firm: '',
    headquarters: '',
  });
  const [contactForm, setContactForm] = useState({ city: '', country: '' });

  const socialList = useMemo(() => {
    if (!Array.isArray(promoter?.socialMedia)) return [];
    return promoter.socialMedia
      .filter((s) => s?.url)
      .map((s) => ({
        key: (s.name || 'link').toLowerCase(),
        label: s.name || 'Link',
        url: s.url,
      }));
  }, [promoter?.socialMedia]);

  const closeEditor = useCallback(() => setEditingSection(null), []);

  const startEditIdentity = useCallback(() => {
    setIdentityForm({ username: promoter?.username || '' });
    setEditingSection('identity');
  }, [promoter?.username]);

  const startEditAbout = useCallback(() => {
    setAboutForm({ profileInfo: promoter?.profileInfo || '' });
    setEditingSection('about');
  }, [promoter?.profileInfo]);

  const startEditBusiness = useCallback(() => {
    setBusinessForm({
      companyName: promoter?.companyName || '',
      fiscalNumber: promoter?.fiscalNumber || '',
      firm: promoter?.firm || '',
      headquarters: promoter?.headquarters || '',
    });
    setEditingSection('business');
  }, [promoter]);

  const startEditContact = useCallback(() => {
    setContactForm({
      city: promoter?.city || '',
      country: promoter?.country || '',
    });
    setEditingSection('contact');
  }, [promoter?.city, promoter?.country]);

  const showSaveError = useCallback(
    (err) => {
      showAlert({
        title: t('dashboard.edit.save_error_title'),
        message: isNameTakenError(err)
          ? t('dashboard.name_taken')
          : t('dashboard.edit.save_error_message'),
        primaryAction: { label: t('dashboard.edit.save_error_action') },
      });
    },
    [showAlert, t]
  );

  const submitFields = useCallback(
    async (entries) => {
      setSaving(true);
      try {
        const fd = new FormData();
        Object.entries(entries).forEach(([k, v]) => {
          if (v === undefined) return;
          fd.append(k, v === null ? '' : v);
        });
        await dispatch(updatePromoter(fd));
        setEditingSection(null);
      } catch (e) {
        showSaveError(e);
      } finally {
        setSaving(false);
      }
    },
    [dispatch, showSaveError]
  );

  const saveIdentity = useCallback(() => {
    const name = identityForm.username.trim();
    if (!name) {
      showAlert({
        type: 'warning',
        title: t('dashboard.edit.save_error_title'),
        message: t('dashboard.name_required', 'Name cannot be empty'),
        primaryAction: { label: t('common.ok') },
      });
      return;
    }
    submitFields({ username: name });
  }, [identityForm, submitFields, showAlert, t]);

  const saveAbout = useCallback(
    () => submitFields({ profileInfo: aboutForm.profileInfo.trim() }),
    [aboutForm, submitFields]
  );
  const saveBusiness = useCallback(
    () =>
      submitFields({
        companyName: businessForm.companyName.trim(),
        fiscalNumber: businessForm.fiscalNumber.trim(),
        firm: businessForm.firm.trim(),
        headquarters: businessForm.headquarters.trim(),
      }),
    [businessForm, submitFields]
  );
  const saveContact = useCallback(
    () =>
      submitFields({
        city: contactForm.city.trim(),
        country: contactForm.country.trim(),
      }),
    [contactForm, submitFields]
  );

  // ---- avatar (crop + immediate save) ----
  const {
    imagePreview,
    croppedImagePreview,
    isCropping,
    handleImageChange,
    handleCropSave,
    setIsCropping,
    resetImage,
  } = useImageCropper();
  const [savingAvatar, setSavingAvatar] = useState(false);

  const onAvatarPick = (e) => {
    const files = Array.from(e.target.files || []);
    const { accepted, errors: uploadErrors } = validateFiles(files, {
      kind: 'image',
      t,
    });
    if (uploadErrors.length) {
      showAlert({
        type: 'warning',
        title: t('upload.error_title', { defaultValue: 'Upload issue' }),
        message: uploadErrors.join('\n'),
        primaryAction: { label: t('common.ok') },
      });
      return;
    }
    if (accepted.length) handleImageChange(e);
  };

  const submitAvatar = async (blob) => {
    setSavingAvatar(true);
    try {
      const fd = new FormData();
      fd.append('avatar', blob, 'avatar.jpg');
      await dispatch(updatePromoter(fd));
    } catch (err) {
      resetImage();
      const uploadMessage = uploadErrorMessage(err, t);
      showAlert({
        title: t('dashboard.edit.save_error_title'),
        message: uploadMessage || t('dashboard.edit.save_error_message'),
        primaryAction: { label: t('dashboard.edit.save_error_action') },
      });
    } finally {
      setSavingAvatar(false);
    }
  };

  useEffect(() => {
    setEditingSection(null);
  }, [promoter?.id]);

  if (loading && !promoter) {
    return (
      <div className="flex items-center justify-center py-16">
        <ClipLoader color="#FFBD59" size={40} />
      </div>
    );
  }

  if (!promoter) {
    return (
      <div className="py-16 text-center text-muted text-sm">
        {error ? `${t('dashboard.error')}${error}` : t('dashboard.no_promoter')}
      </div>
    );
  }

  // username first — it's what public surfaces (event detail, booking cards,
  // sidebar) show, and it's the field the identity editor updates
  const displayName =
    promoter.username || promoter.companyName || t('dashboard.no_promoter');
  const locationStr = [promoter.city, promoter.country]
    .filter(Boolean)
    .join(', ');
  const websiteHref =
    promoter.website && !/^https?:\/\//i.test(promoter.website)
      ? `https://${promoter.website}`
      : promoter.website || null;

  const isEditing = (section) => editingSection === section;
  const canStartEditing = editingSection === null;

  return (
    <div className="space-y-6">
      <TabHeader
        eyebrow={t('dashboard.info.promoter_eyebrow')}
        title={displayName}
        sub={t('dashboard.info.subtitle')}
      />

      {/* Identity card */}
      <Card padded={false} className="overflow-hidden">
        <div className="relative">
          <div className="h-24 sm:h-28 bg-gradient-to-br from-brand-soft via-brand-softer to-paper" />
          <div className="px-5 sm:px-7 pb-6 -mt-12 flex flex-col sm:flex-row sm:items-end gap-4 sm:gap-5 flex-wrap">
            <div className="relative shrink-0 mx-auto sm:mx-0">
              <div className="w-[88px] h-[88px] rounded-full bg-white p-1 shadow-card">
                {croppedImagePreview || promoter.avatar ? (
                  <img
                    src={croppedImagePreview || promoter.avatar}
                    alt=""
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full rounded-full bg-brand text-white flex items-center justify-center font-bold text-[28px]">
                    {initialsOf(displayName)}
                  </div>
                )}
              </div>
              <input
                id="promoter-avatar-input"
                type="file"
                accept={IMAGE_ACCEPT}
                className="sr-only"
                disabled={savingAvatar}
                onChange={(e) => {
                  onAvatarPick(e);
                  e.target.value = '';
                }}
              />
              <label
                htmlFor="promoter-avatar-input"
                className="absolute bottom-0 right-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-white border border-line shadow-softer cursor-pointer hover:bg-paper-2"
                title={t('dashboard.change_avatar', 'Change photo')}
              >
                {savingAvatar ? (
                  <ClipLoader color="#9CA3AF" size={14} />
                ) : (
                  <CameraIcon className="w-4 h-4 text-ink" />
                )}
              </label>
            </div>
            <div className="flex-1 min-w-0 text-center sm:text-left">
              {isEditing('identity') ? (
                <div className="max-w-md mx-auto sm:mx-0 text-left">
                  <FieldLabel>
                    {t('dashboard.username', 'Username')}
                  </FieldLabel>
                  <TextInput
                    value={identityForm.username}
                    maxLength={100}
                    onChange={(e) =>
                      setIdentityForm({ username: e.target.value })
                    }
                  />
                  <EditActions
                    onCancel={closeEditor}
                    onSave={saveIdentity}
                    saving={saving}
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center sm:justify-start gap-2 flex-wrap">
                  <h2
                    className="text-[22px] sm:text-[26px] font-bold tracking-tight text-ink-strong leading-tight break-words"
                    title={displayName}
                  >
                    {displayName}
                  </h2>
                  {promoter.emailConfirmed && (
                    <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.08em] font-bold text-emerald-700 bg-emerald-50 rounded-full px-2.5 py-1">
                      <CheckBadgeIcon className="w-3.5 h-3.5" />
                      {t('dashboard.verified')}
                    </span>
                  )}
                  {canStartEditing && (
                    <PencilBtn
                      onClick={startEditIdentity}
                      label={t('dashboard.edit_profile')}
                    />
                  )}
                </div>
              )}
              <div className="flex items-center justify-center sm:justify-start gap-4 mt-2 text-[12.5px] text-muted flex-wrap">
                <span className="inline-flex items-center gap-1.5">
                  <TagIcon className="w-3.5 h-3.5" />
                  {t('dashboard.info.promoter_type')}
                </span>
                {locationStr && (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPinIcon className="w-3.5 h-3.5" />
                    {locationStr}
                  </span>
                )}
                {promoter.headquarters && (
                  <span className="inline-flex items-center gap-1.5">
                    <BuildingOffice2Icon className="w-3.5 h-3.5" />
                    {promoter.headquarters}
                  </span>
                )}
              </div>
            </div>
            {/* Profile completeness — hidden for now, will return once we
                decide what the % should reflect.
            <div className="w-full sm:w-[220px] mb-1">
              ...
            </div>
            */}
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-6 min-w-0">
          {/* About — inline */}
          <Card>
            <CardHeader
              eyebrow={t('dashboard.info.about_eyebrow')}
              title={t('dashboard.info.description_title')}
              action={
                isEditing('about') ? null : (
                  canStartEditing && (
                    <PencilBtn
                      onClick={startEditAbout}
                      label={t('dashboard.edit_profile')}
                    />
                  )
                )
              }
            />
            {isEditing('about') ? (
              <div className="space-y-4">
                <Textarea
                  value={aboutForm.profileInfo}
                  onChange={(e) =>
                    setAboutForm({
                      profileInfo: e.target.value.slice(0, 2000),
                    })
                  }
                  maxLength={2000}
                />
                <div className="text-[11px] text-muted">
                  {aboutForm.profileInfo.length}/2000
                </div>
                <EditActions
                  onCancel={closeEditor}
                  onSave={saveAbout}
                  saving={saving}
                />
              </div>
            ) : (
              <p className="text-[14.5px] leading-relaxed text-ink whitespace-pre-wrap">
                {promoter.profileInfo || t('dashboard.no_description')}
              </p>
            )}
          </Card>

          {/* Business details — inline */}
          <Card>
            <CardHeader
              eyebrow={t('dashboard.info.business_eyebrow')}
              title={t('dashboard.info.business_title')}
              action={
                isEditing('business') ? null : (
                  canStartEditing && (
                    <PencilBtn
                      onClick={startEditBusiness}
                      label={t('dashboard.edit_profile')}
                    />
                  )
                )
              }
            />
            {isEditing('business') ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <FieldLabel>
                    {t('dashboard.info.company_name_label')}
                  </FieldLabel>
                  <TextInput
                    value={businessForm.companyName}
                    onChange={(e) =>
                      setBusinessForm((p) => ({
                        ...p,
                        companyName: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <FieldLabel>
                    {t('dashboard.info.fiscal_number_label')}
                  </FieldLabel>
                  <TextInput
                    value={businessForm.fiscalNumber}
                    onChange={(e) =>
                      setBusinessForm((p) => ({
                        ...p,
                        fiscalNumber: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <FieldLabel>{t('dashboard.info.firm_label')}</FieldLabel>
                  <TextInput
                    value={businessForm.firm}
                    onChange={(e) =>
                      setBusinessForm((p) => ({
                        ...p,
                        firm: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <FieldLabel>
                    {t('dashboard.info.headquarters_label')}
                  </FieldLabel>
                  <TextInput
                    value={businessForm.headquarters}
                    onChange={(e) =>
                      setBusinessForm((p) => ({
                        ...p,
                        headquarters: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="sm:col-span-2">
                  <EditActions
                    onCancel={closeEditor}
                    onSave={saveBusiness}
                    saving={saving}
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InfoCell
                  icon={BuildingOffice2Icon}
                  label={t('dashboard.info.company_name_label')}
                  value={promoter.companyName}
                />
                <InfoCell
                  icon={IdentificationIcon}
                  label={t('dashboard.info.fiscal_number_label')}
                  value={promoter.fiscalNumber}
                  mono
                />
                <InfoCell
                  icon={TagIcon}
                  label={t('dashboard.info.firm_label')}
                  value={promoter.firm}
                />
                <InfoCell
                  icon={MapPinIcon}
                  label={t('dashboard.info.headquarters_label')}
                  value={promoter.headquarters}
                />
              </div>
            )}
          </Card>
        </div>

        {/* Right column */}
        <div className="space-y-6 min-w-0">
          {/* Contact — city/country inline, email locked */}
          <Card>
            <CardHeader
              eyebrow={t('dashboard.info.contact_eyebrow')}
              title={t('dashboard.info.contact_title')}
              action={
                isEditing('contact') ? null : (
                  canStartEditing && (
                    <PencilBtn
                      onClick={startEditContact}
                      label={t('dashboard.edit_profile')}
                    />
                  )
                )
              }
            />
            {isEditing('contact') ? (
              <div className="space-y-4">
                <div>
                  <FieldLabel>{t('dashboard.info.email_label')}</FieldLabel>
                  <div className="flex items-center gap-2 px-3.5 h-11 rounded-xl bg-paper-2 border border-line text-[14px] text-muted">
                    <LockClosedIcon className="w-4 h-4 shrink-0" />
                    <span className="truncate">{promoter.email || '—'}</span>
                  </div>
                  <div className="text-[11px] text-muted mt-1">
                    {t('dashboard.edit.email_locked')}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <FieldLabel>{t('dashboard.city')}</FieldLabel>
                    <TextInput
                      value={contactForm.city}
                      onChange={(e) =>
                        setContactForm((p) => ({
                          ...p,
                          city: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div>
                    <FieldLabel>{t('dashboard.country')}</FieldLabel>
                    <TextInput
                      value={contactForm.country}
                      onChange={(e) =>
                        setContactForm((p) => ({
                          ...p,
                          country: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
                <EditActions
                  onCancel={closeEditor}
                  onSave={saveContact}
                  saving={saving}
                />
              </div>
            ) : (
              <div className="space-y-3">
                <InfoCell
                  icon={EnvelopeIcon}
                  label={t('dashboard.info.email_label')}
                  value={promoter.email}
                  href={promoter.email ? `mailto:${promoter.email}` : null}
                  locked
                />
                {promoter.website && (
                  <InfoCell
                    icon={GlobeAltIcon}
                    label={t('dashboard.info.website_label')}
                    value={promoter.website}
                    href={websiteHref}
                  />
                )}
                <InfoCell
                  icon={MapPinIcon}
                  label={t('dashboard.info.location_label')}
                  value={locationStr}
                />
              </div>
            )}
          </Card>

          {/* Social — read-only for now (backend endpoint doesn't accept it) */}
          <Card>
            <CardHeader
              eyebrow={t('dashboard.info.social_eyebrow')}
              title={t('dashboard.info.social_title')}
              action={
                onEdit && (
                  <button
                    type="button"
                    onClick={onEdit}
                    className="w-9 h-9 rounded-full hover:bg-paper-2 inline-flex items-center justify-center text-ink"
                    aria-label={t('dashboard.edit_profile')}
                  >
                    <PlusIcon className="w-[18px] h-[18px]" />
                  </button>
                )
              }
            />
            {socialList.length > 0 ? (
              <div className="space-y-2.5">
                {socialList.map((s, idx) => {
                  const iconSrc = SOCIAL_ICONS[s.key];
                  return (
                    <a
                      key={`${s.key}-${idx}`}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between gap-3 p-2.5 -mx-1 rounded-xl hover:bg-paper-2 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-lg bg-brand-softer flex items-center justify-center shrink-0">
                          {iconSrc ? (
                            <img src={iconSrc} alt="" className="w-4 h-4" />
                          ) : (
                            <LinkIcon className="w-4 h-4 text-yellow-03" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="text-[13.5px] font-bold text-ink-strong">
                            {s.label}
                          </div>
                          <div
                            className="text-[12px] text-muted truncate"
                            title={s.url}
                          >
                            {s.url}
                          </div>
                        </div>
                      </div>
                      <ArrowRightIcon className="w-4 h-4 text-muted shrink-0" />
                    </a>
                  );
                })}
              </div>
            ) : (
              <p className="text-[13.5px] text-muted italic">
                {t('dashboard.info.no_social')}
              </p>
            )}
          </Card>
        </div>
      </div>

      {isCropping && imagePreview && (
        <ImageCropper
          imageSrc={imagePreview}
          aspect={1}
          shape="round"
          onCropSave={async (output) => {
            const blob = await handleCropSave(output);
            if (blob) await submitAvatar(blob);
          }}
          onClose={() => {
            setIsCropping(false);
            resetImage();
          }}
        />
      )}
    </div>
  );
};

export default PromoterInfo;
